import React, {
  type AnchorHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type InputHTMLAttributes,
  type ReactComponentElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import classNames from "classnames";

import { useClickOutside } from "../../hooks/clickOutside";
import { Stack } from "../../layouts/Stack/Stack";
import { Switch } from "../Switch/Switch";

import style from "./Menu.module.css";

type MenuChildren = {
  Item: typeof MenuItem;
  Link: typeof MenuLink;
  Divider: typeof MenuDivider;
  Toggle: typeof MenuToggle;
};

type MenuComponent<T> = React.FC<T> & {
  [key in keyof MenuChildren]: MenuChildren[key];
};

export const useDropdownPlacement = (
  open: boolean,
  activatorRef: RefObject<HTMLElement>,
  menuRef: RefObject<HTMLDivElement>,
  dropdownPosition?: "left" | "right"
): CSSProperties => {
  const [style, setStyle] = useState<CSSProperties>({});
  const updatePosition = useCallback(() => {
    if (!open || !activatorRef.current || !menuRef.current) return;

    const activatorRect = activatorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewPortWidth = window.innerWidth;
    const viewPortHeight = window.innerHeight;
    const dropdownVerticalOffset = 4;
    const dropdownHorizontalOffset = 0;

    let top: number;
    const spaceBelow = viewPortHeight - activatorRect.bottom;

    if (menuRect.height <= spaceBelow) {
      top = activatorRect.bottom + dropdownVerticalOffset;
    } else {
      top = activatorRect.top - menuRect.height - dropdownVerticalOffset;
    }

    let left: number;
    if (dropdownPosition === "left") {
      left = activatorRect.right - menuRect.width - dropdownHorizontalOffset;
      if (left < 0) {
        left = activatorRect.left + dropdownHorizontalOffset;
      }
    } else {
      left = activatorRect.left + dropdownHorizontalOffset;
      if (left + menuRect.width > viewPortWidth) {
        left = activatorRect.right - menuRect.width - dropdownHorizontalOffset;
      }
    }

    setStyle({ top, left });
  }, [open, dropdownPosition, activatorRef, menuRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  const updatePositionCallback = useCallback(() => {
    requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener("scroll", updatePositionCallback);
    window.addEventListener("resize", updatePositionCallback);

    return () => {
      window.removeEventListener("scroll", updatePositionCallback);
      window.removeEventListener("resize", updatePositionCallback);
    };
  }, [open, updatePositionCallback]);

  return style;
};

export const Menu: MenuComponent<{
  children: ReactNode;
  activator: ReactComponentElement<React.FC<{ onClick: React.MouseEventHandler }>>;
  onOpen?: () => void;
  onClose?: () => void;
  onClick?: React.MouseEventHandler;
  matchActivatorWidth?: boolean;
  dropdownPosition?: "left" | "right";
  className?: string;
}> = ({
  children,
  className,
  activator,
  matchActivatorWidth,
  onOpen,
  onClose,
  onClick,
  dropdownPosition = "right",
}) => {
  const [open, setOpen] = useState(false);
  const clickOutsideTarget = useRef(null);

  useClickOutside(clickOutsideTarget.current, () => {
    setOpen(false);
    if (open) onClose?.();
  });

  return (
    <div ref={clickOutsideTarget} className={classNames(style.menuWrapper, className)}>
      {React.cloneElement(activator, {
        onClick: (event: React.MouseEvent) => {
          open ? onClose?.() : onOpen?.();
          setOpen(!open);
          activator.props.onClick?.(event);
        },
      })}
      <div
        role="menu"
        className={classNames(style.dropdownWrapper, open && style.open)}
        onClick={onClick}
      >
        <Stack
          className={classNames(
            style.menu,
            dropdownPosition === "right" ? style.right : style.left,
            matchActivatorWidth && style.fullWidth
          )}
          direction={"column"}
        >
          {React.Children.map(children, (child) => {
            if (child && React.isValidElement(child) && child.props.closeOnClick) {
              const c = child as ReactComponentElement<
                React.FC<{ onClick: (event: unknown) => void }>
              >;

              return React.cloneElement(c, {
                onClick: (event: unknown) => {
                  open ? onClose?.() : onOpen?.();
                  setOpen(!open);
                  c.props.onClick?.(event);
                },
              });
            }

            return child;
          })}
        </Stack>
      </div>
    </div>
  );
};

const MenuItem: React.FC<{
  children: ReactNode;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  closeOnClick?: boolean;
  className?: string;
}> = ({ children, disabled, onClick, className }) => {
  return (
    <button
      role="menuitem"
      className={classNames(style.menuItem, className)}
      disabled={disabled}
      onClick={(event: React.MouseEvent) => onClick?.(event)}
    >
      {children}
    </button>
  );
};
Menu.Item = MenuItem;

const MenuLink: React.FC<
  {
    children: ReactNode;
    closeOnClick?: boolean;
  } & AnchorHTMLAttributes<HTMLAnchorElement>
> = ({ children, closeOnClick: _, ...attrs }) => {
  return (
    <a role="menuitem" {...attrs} className={classNames(style.menuItem)}>
      {children}
    </a>
  );
};
Menu.Link = MenuLink;

const MenuDivider: React.FC = () => {
  return <hr role="separator" className={style.menuDivider}></hr>;
};
Menu.Divider = MenuDivider;

const MenuToggle = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { closeOnClick?: boolean }
>(({ children, className, closeOnClick: _, ...attrs }, ref) => {
  return (
    <label
      role="menuitemcheckbox"
      className={classNames(
        style.menuToggle,
        style.menuItem,
        attrs.disabled && style.disabled,
        className
      )}
    >
      {children}
      <Switch {...attrs} ref={ref} />
    </label>
  );
});
MenuToggle.displayName = "MenuToggle";
Menu.Toggle = MenuToggle;
